import { Controller } from '@hotwired/stimulus'

// Buyer variant picker supports:
//   color_only      → one tap (clothing colours)
//   size_then_color → pick size, then colour
//   color_then_size → legacy colour-first flow
//   size_only       → one tap (sizes only)
//   single          → flat list fallback
export default class extends Controller {
  static targets = [
    'option', 'input', 'quantity', 'submit', 'stockLabel', 'prompt', 'priceDisplay',
    'coinsDisplay', 'colorStep', 'sizeStep', 'sizeOptions', 'colorOptions', 'colorPrompt',
    'sizePrompt'
  ]

  static values = {
    mode: { type: String, default: 'single' },
    variants: { type: Array, default: [] },
    pricesVary: { type: Boolean, default: false },
    coinsPerRupee: Number
  }

  static classes = ['selected']

  connect () {
    this.selectedId = null
    this.selectedColor = null
    this.selectedSize = null
    this.updateOrderability()
  }

  select (event) {
    const button = event.currentTarget
    if (button.dataset.inStock === 'false') return
    const priceCents = parseInt(button.dataset.priceCents || '0', 10)
    this.applySelection(
      button.dataset.variantId,
      parseInt(button.dataset.stock || '0', 10),
      button.dataset.variantLabel || '',
      priceCents
    )
    this.highlightOptions(this.optionTargets, button)
  }

  selectColor (event) {
    const button = event.currentTarget
    if (button.dataset.inStock === 'false') return

    this.selectedColor = button.dataset.color
    this.selectedId = null
    this.highlightOptions(this.colorStepTarget.querySelectorAll('[data-variant-picker-target="option"]'), button)

    this.renderSizesForColor(this.selectedColor)
    if (this.hasSizeStepTarget) this.sizeStepTarget.classList.remove('hidden')
    if (this.hasColorPromptTarget) this.colorPromptTarget.classList.add('hidden')
    if (this.hasSizePromptTarget) this.sizePromptTarget.classList.remove('hidden')

    this.clearInputs()
    this.updateOrderability()
    this.updateStockLabel('')
  }

  selectSize (event) {
    const button = event.currentTarget
    if (button.dataset.inStock === 'false') return
    const priceCents = parseInt(button.dataset.priceCents || '0', 10)
    this.applySelection(
      button.dataset.variantId,
      parseInt(button.dataset.stock || '0', 10),
      button.dataset.variantLabel || '',
      priceCents
    )
    this.highlightOptions(this.sizeOptionsTarget.querySelectorAll('button'), button)
  }

  selectSizeFirst (event) {
    const button = event.currentTarget
    if (button.dataset.inStock === 'false') return

    this.selectedSize = button.dataset.size || ''
    this.selectedId = null
    this.highlightOptions(this.sizeStepTarget.querySelectorAll('[data-variant-picker-target="option"]'), button)

    this.renderColorsForSize(this.selectedSize)
    if (this.hasColorStepTarget) this.colorStepTarget.classList.remove('hidden')
    if (this.hasSizePromptTarget) this.sizePromptTarget.classList.add('hidden')
    if (this.hasColorPromptTarget) this.colorPromptTarget.classList.remove('hidden')

    this.clearInputs()
    this.updateOrderability()
    this.updateStockLabel('')
  }

  selectColorForSize (event) {
    const button = event.currentTarget
    if (button.dataset.inStock === 'false') return
    const priceCents = parseInt(button.dataset.priceCents || '0', 10)
    this.applySelection(
      button.dataset.variantId,
      parseInt(button.dataset.stock || '0', 10),
      button.dataset.variantLabel || '',
      priceCents
    )
    this.highlightOptions(this.colorOptionsTarget.querySelectorAll('button'), button)
  }

  renderSizesForColor (color) {
    if (!this.hasSizeOptionsTarget) return

    const sizes = this.variantsValue
      .filter((v) => (v.color || '') === color)
      .sort((a, b) => this.compareSizes(a.size, b.size))

    this.sizeOptionsTarget.innerHTML = sizes.map((v) => {
      const inStock = v.stock > 0
      const disabled = inStock ? '' : 'disabled'
      const outBadge = inStock ? '' : '<span class="mt-0.5 block text-[8px] font-bold uppercase leading-none tracking-wide text-slate-400">Out</span>'
      const cls = inStock
        ? 'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition border-slate-200 bg-white text-slate-700 hover:border-brand-300 hover:bg-brand-50/60'
        : 'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300 line-through'

      return `<button type="button"
                      data-action="variant-picker#selectSize"
                      data-variant-id="${v.id}"
                      data-stock="${v.stock}"
                      data-variant-label="${this.escapeHtml(v.label)}"
                      data-price-cents="${v.price || 0}"
                      data-in-stock="${inStock}"
                      aria-pressed="false"
                      ${disabled}
                      class="relative min-w-[3rem] rounded-xl border px-3.5 py-2.5 text-sm font-bold transition ${cls}">
                ${this.escapeHtml(v.size || v.label)}${outBadge}
              </button>`
    }).join('')
  }

  renderColorsForSize (size) {
    if (!this.hasColorOptionsTarget) return

    const colors = this.variantsValue
      .filter((v) => ((v.size || '') || '__none__') === size)
      .sort((a, b) => (a.color || '').localeCompare((b.color || ''), undefined, { numeric: true }))

    this.colorOptionsTarget.innerHTML = colors.map((v) => {
      const inStock = v.stock > 0
      const disabled = inStock ? '' : 'disabled'
      const outBadge = inStock ? '' : '<span class="text-[10px] font-bold uppercase text-slate-400">Out</span>'
      const swatch = v.swatch || '#e2e8f0'
      const cls = inStock
        ? 'inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-bold text-slate-700 transition hover:border-brand-300 hover:bg-brand-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 sm:py-2'
        : 'inline-flex shrink-0 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-1.5 text-sm font-bold text-slate-300 line-through sm:py-2'

      return `<button type="button"
                      data-action="variant-picker#selectColorForSize"
                      data-variant-id="${v.id}"
                      data-stock="${v.stock}"
                      data-variant-label="${this.escapeHtml(v.label)}"
                      data-price-cents="${v.price || 0}"
                      data-in-stock="${inStock}"
                      aria-pressed="false"
                      ${disabled}
                      class="${cls}">
                <span class="h-4 w-4 shrink-0 rounded-full ring-1 ring-slate-200/80" style="background-color: ${this.escapeHtml(swatch)};" aria-hidden="true"></span>
                ${this.escapeHtml(v.color || v.label)}${outBadge}
              </button>`
    }).join('')
  }

  applySelection (variantId, stock, label, priceCents = 0) {
    this.selectedId = variantId
    this.inputTargets.forEach((input) => { input.value = variantId })

    this.quantityTargets.forEach((qty) => {
      qty.max = stock
      if (parseInt(qty.value || '1', 10) > stock) qty.value = stock
      if (parseInt(qty.value || '0', 10) < 1) qty.value = 1
    })

    this.updateStockLabel(label, stock)
    if (priceCents > 0) this.updatePriceDisplay(priceCents)
    this.updateOrderability()
    if (this.hasPromptTarget) this.promptTarget.classList.add('hidden')
    if (this.hasSizePromptTarget) this.sizePromptTarget.classList.add('hidden')
    if (this.hasColorPromptTarget) this.colorPromptTarget.classList.add('hidden')
  }

  clearInputs () {
    this.inputTargets.forEach((input) => { input.value = '' })
  }

  highlightOptions (nodes, activeButton) {
    const selected = this.selectedClasses.length
      ? this.selectedClasses
      : ['ring-2', 'ring-brand-200', 'border-brand-500', 'bg-brand-50', 'text-brand-800']
    const idle = ['border-slate-200', 'bg-white', 'text-slate-700']

    nodes.forEach((opt) => {
      const active = opt === activeButton
      opt.setAttribute('aria-pressed', active ? 'true' : 'false')
      if (active) {
        opt.classList.add(...selected)
        opt.classList.remove(...idle)
      } else {
        opt.classList.remove(...selected)
        opt.classList.add(...idle)
      }
    })
  }

  updateStockLabel (label, stock) {
    if (!this.hasStockLabelTarget) return
    if (!label) {
      this.stockLabelTarget.textContent = ''
      return
    }
    this.stockLabelTarget.textContent = stock > 0 ? `${label}: ${stock} in stock` : 'Out of stock'
  }

  updatePriceDisplay (priceCents) {
    if (!this.hasPriceDisplayTarget || !priceCents) return
    this.priceDisplayTarget.textContent = this.formatPkr(priceCents)
    if (this.hasCoinsDisplayTarget && this.hasCoinsPerRupeeValue) {
      this.coinsDisplayTarget.textContent = this.formatCoins(Math.floor(priceCents * this.coinsPerRupeeValue / 100))
    }
  }

  formatPkr (cents) {
    const rupees = Math.round(cents / 100)
    return `Rs ${rupees.toLocaleString('en-PK')}`
  }

  formatCoins (amount) {
    return Number(amount || 0).toLocaleString('en-PK')
  }

  updateOrderability () {
    const ready = Boolean(this.selectedId)
    this.submitTargets.forEach((btn) => {
      btn.disabled = !ready
      btn.classList.toggle('opacity-50', !ready)
      btn.classList.toggle('cursor-not-allowed', !ready)
      btn.classList.toggle('cursor-pointer', ready)
    })

    if (this.modeValue !== 'color_then_size' && this.hasPromptTarget) {
      this.promptTarget.classList.toggle('hidden', ready)
    }
  }

  compareSizes (a, b) {
    const sa = (a || '').toString()
    const sb = (b || '').toString()
    const na = parseFloat(sa)
    const nb = parseFloat(sb)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
    return sa.localeCompare(sb, undefined, { numeric: true })
  }

  escapeHtml (text) {
    return (text || '').toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
